import './index.less';
import classNames from 'classnames';
import { cloneDeep, debounce } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import svgPath from 'svgpath';
import CurPenSvg from '@/assets/cur-pen.svg';
import {
    BaseStagePlugin,
    checkPointInStage,
    checkPointInViewport,
    convertToLayerPoint,
    convertToStagePoint,
    getFramework,
    getStageIndexByMousePointY,
    HOTKEY_TYPE,
    MoveListenerLevel,
    trackConfig,
    useActiveOriginIndex,
    useActiveStageIndex,
    useIdentity,
    useIsLongImageMode,
    useMoveEventListener,
    useScale,
} from '@/framework';
import { useLocaleStore } from '@/i18n/index';
import {
    addVipMaterialIds,
    BrushLayer,
    BrushLayerView,
    createPathTarget,
    exportBrushPath,
    generateBrushPath,
    generateLinePath,
    getBrushBaseInfo,
    getBrushDefaultLayer,
    getDefaultBrushAttribute,
    getPathOffset,
    isArrowSvg,
    isCurveSvg,
    scaleConfig,
    updateScreenshotUrl,
} from '@/layers/brush-layer';
import VectorLayer from '@/layers/vector-layer/views/vector-layer';
import { getLastMousePoint, isHotKeyPress, ITouchKitEvent } from '@/utils/event-kit';
import { fetchSvgContentWithCache, formatSvgPathDecimals } from '@/utils/file-kit';
import { convertBrushToVectorLayer } from '@/utils/layer-kit';
import {
    BaseLayerViewType,
    BrushDasharrayType,
    BrushLinecapType,
    BrushType,
    IBrushDrawConfig,
    IBrushLayerProps,
    IExportPlugin,
    ILayerProps,
    IPoint,
    LayerType,
    PluginType,
} from '@/version-types';
import { BrushDrawPlugin } from './brush-draw';
import { DRAW_EVENTS } from './event-name';
import brushDrawPluginState, { getBrushLayerState, useBrushLayerState } from './states/brush-state';

const DrawOperate: React.FC = React.memo(() => {
    const identity = useIdentity();
    const scale = useScale();
    const activeStageIndex = useActiveStageIndex();
    const activeOriginIndex = useActiveOriginIndex();
    const isLongImageMode = useIsLongImageMode();
    const framework = getFramework(identity);

    /** 第一次是否点击在画布中间 */
    const mousedownInStage = useRef(false);
    /** 绘制的鼠标点 */
    const mousePoints = useRef<IPoint[]>([]);
    /** 绘制时的预览图层 */
    const preview = useRef<ILayerProps>();
    /** 画笔属性 */
    const drawConfig = useBrushLayerState()((state) => state.drawConfig);
    /** 渲染预览图层数据 */
    const [previewLayer, setPreviewLayer] = useState<ILayerProps | null>(null);
    /** 远程素材里的 path */
    const brushUrlPath = useRef('');
    /** 自定义鼠标样式 */
    const [penPoint, setPenPoint] = useState<IPoint | null>(null);
    /** 画笔实时属性 */
    const [brushData, setDrawBrushData] = useState<IBrushDrawConfig | null>(null);
    /** 当前舞台宽 */
    const currentWidth = framework.getSizeByIndex(activeStageIndex).width;
    const isVectorLayer = useRef(false);
    const isDrawing = useRef(false);
    const { $t } = useLocaleStore();
    const drawTips = {
        tip: $t((v) => v.brushLayer.tips.moveToCanvas),
    };

    /** 根据舞台大小，等比例缩放配置 */
    useEffect(() => {
        if (drawConfig && !drawConfig.__custom && currentWidth) {
            setDrawBrushData(scaleConfig(drawConfig, currentWidth));
        } else if (drawConfig) {
            setDrawBrushData(scaleConfig(drawConfig));
        }
    }, [currentWidth, drawConfig]);

    useEffect(() => {
        // 画笔切换，加载远程路径
        brushUrlPath.current = '';
        if (!brushData) return;
        if (brushData.url) {
            fetchSvgContentWithCache(brushData.url).then((text) => {
                const pathD = exportBrushPath(text);
                if (pathD) {
                    brushUrlPath.current = pathD;
                }
            });
        } else {
            brushUrlPath.current = '-';
        }
    }, [brushData]);

    useEffect(() => {
        if (!drawConfig) return;
        const handle = framework.events.onEsc(() => {
            clearDrawConfig();
        });
        return () => {
            framework.events.off(handle);
        };
    }, [drawConfig]);

    useEffect(() => {
        if (!brushData) return;
        // 禁用图层选中事件，取消激活图层
        framework.operate.disablePick();
        framework.operate.delHoverId();
        framework.stage.setIsPickStage(false);

        const mouseDownHandle = (e) => {
            showDrawTip(e, true);
        };

        const mouseEnterHandler = framework.events.onMouseEnter((originIndex) => {
            if (!isDrawing.current && framework.stage.isLongImageMode()) {
                framework.stage.setActiveStageIndex(framework.convertOriginToStage(originIndex));
            }
        });

        if (brushData.__isQuickDraw && !isDrawing.current && isLongImageMode) {
            const lastMousePoint = getLastMousePoint();
            const currentStageIndex = getStageIndexByMousePointY(identity, lastMousePoint.y);
            const stageIndex = framework.stage.getActiveStageIndex();
            if (currentStageIndex !== stageIndex) {
                framework.stage.setActiveStageIndex(currentStageIndex);
            }
        }

        const mouseMoveHandle = debounce(
            (e) => {
                if (!brushData) return;
                const mPoint = { x: e.x, y: e.y };
                const stageIndex = framework.stage.getActiveStageIndex();
                const isInStage = checkPointInStage(identity, mPoint, stageIndex);
                if (isInStage) {
                    if (isArrowSvg(brushData.brushType) || isCurveSvg(brushData.brushType)) {
                        setPenPoint(mPoint);
                    } else {
                        setPenPoint(null);
                    }
                } else {
                    setPenPoint(null);
                }
                showDrawTip(e);
            },
            5,
            { leading: true },
        );

        document.addEventListener('mousedown', mouseDownHandle);
        document.addEventListener('mousemove', mouseMoveHandle);
        return () => {
            framework.operate.enablePick();
            framework.operate.cursorTip.clear();
            framework.events.off(mouseEnterHandler);
            document.removeEventListener('mousedown', mouseDownHandle);
            document.removeEventListener('mousemove', mouseMoveHandle);
        };
    }, [brushData]);

    useMoveEventListener(
        (e: ITouchKitEvent) => {
            if (!brushData || !brushUrlPath.current) return;
            let startInStage = mousedownInStage.current;
            let previewProps = preview.current;
            const stageIndex = framework.stage.getActiveStageIndex();
            if (e.isStart) {
                framework.operate.disablePick();
                startInStage = checkPointInStage(identity, e.startPoint, stageIndex);
                mousedownInStage.current = startInStage;
                preview.current = getBrushDefaultLayer(brushData);
                previewProps = preview.current;
            }
            if (e.isEnd) {
                framework.operate.enablePick();
            }
            if (!startInStage) return;
            isDrawing.current = true;
            const mousePoint = convertToLayerPoint(identity, e.currentPoint, stageIndex);
            // 判断点是否有效
            let isValidPoint = true;
            if (mousePoints.current.length) {
                const lastPoint = mousePoints.current[mousePoints.current.length - 1];
                if (lastPoint.x === mousePoint.x && lastPoint.y === mousePoint.y) {
                    isValidPoint = false;
                }
            }
            if (isValidPoint) {
                if (brushData.brushType === BrushType.Arrow || brushData.brushType === BrushType.Line) {
                    const hotKeyPress = isHotKeyPress();
                    // 绘制 90 180 的角度
                    if (hotKeyPress && mousePoints.current.length) {
                        const firstPoint = mousePoints.current[0];
                        const xLength = Math.abs(mousePoint.x - firstPoint.x);
                        const yLength = Math.abs(mousePoint.y - firstPoint.y);
                        if (xLength > yLength) {
                            mousePoint.y = firstPoint.y;
                        } else {
                            mousePoint.x = firstPoint.x;
                        }
                    }
                }
                mousePoints.current.push(mousePoint);
            }

            if (previewProps) {
                const info = getBrushBaseInfo(
                    identity,
                    brushData,
                    scale,
                    mousePoints.current[0],
                    mousePoints.current[mousePoints.current.length - 1],
                    stageIndex,
                );
                Object.assign(previewProps, {
                    width: ~~info.width,
                    height: ~~info.height,
                    left: ~~info.left,
                    top: ~~info.top,
                    rotate: info.rotate,
                });
                Object.assign(previewProps.data, {
                    naturalWidth: info.naturalWidth,
                    naturalHeight: info.naturalHeight,
                });
                // 生成绘制路径
                (previewProps.data as IBrushLayerProps).__drawPath = generateBrushPath(mousePoints.current, 0)[
                    brushData.brushType
                ](brushUrlPath.current) as string;
                // 添加预览标记
                (previewProps.data as IBrushLayerProps).__isDrawPreview = true;
                // 执行预览绘制
                const preLayer = cloneDeep(previewProps);
                const vectorPreLayer = convertBrushToVectorLayer(preLayer);
                isVectorLayer.current = !!vectorPreLayer;
                setPreviewLayer(vectorPreLayer || preLayer);
            }

            if (e.isEnd) {
                if (previewProps) {
                    if (brushData.brushType === BrushType.Curve) {
                        // 曲线平滑处理
                        (previewProps.data as IBrushLayerProps).__drawPath = generateBrushPath(
                            mousePoints.current,
                            // 获取抖动值
                            currentWidth / 200,
                        )[brushData.brushType]()!;
                    }
                    // 转成图层绘制
                    convertPreviewPropsToLayer(cloneDeep(previewProps));
                }
                setPreviewLayer(null);
                mousedownInStage.current = false;
                mousePoints.current = [];
                isDrawing.current = false;
            }
        },
        MoveListenerLevel.DEFAULT_LEVEL,
        'brush-draw-default',
    );

    // 绘制提示
    function showDrawTip(e: any, needDestroy = false) {
        if (!brushData) return;
        const mPoint = { x: e.x, y: e.y };
        if (!checkPointInViewport(identity, mPoint)) {
            framework.operate.cursorTip.clear();
            return;
        }
        const stageIndex = framework.stage.getActiveStageIndex();
        const isInStage = checkPointInStage(identity, mPoint, stageIndex);
        if (!isInStage && !isDrawing.current) {
            if (needDestroy) {
                clearDrawConfig();
            }
            framework.operate.cursorTip.set({
                type: 'custom',
                x: mPoint.x,
                y: mPoint.y,
                txt: drawTips.tip,
            });
        } else {
            framework.operate.cursorTip.clear();
        }
    }

    function clearDrawConfig() {
        getBrushLayerState(identity).setDrawConfig(null);
    }

    function convertPreviewPropsToLayer(layer: ILayerProps) {
        const data = layer.data as IBrushLayerProps;
        delete data.__isDrawPreview;
        if (!data.__drawPath) return;
        const stageIndex = framework.stage.getActiveStageIndex();
        const brushType = data.brushType;
        if (brushType === BrushType.Curve) {
            const path = createPathTarget(
                {
                    ...data.fills[0],
                    width: 1,
                },
                data.__drawPath!,
                layer.width,
                layer.height,
                [],
            );
            const pathTransform = getPathTransform(path, scale);
            const position = convertToLayerPoint(
                identity,
                {
                    x: pathTransform.left,
                    y: pathTransform.top,
                },
                stageIndex,
            );
            data.naturalWidth = pathTransform.width;
            data.naturalHeight = pathTransform.height;
            if (data.__drawPath) {
                // 对路径进行位移
                data.__drawPath = formatSvgPathDecimals(
                    svgPath(data.__drawPath).translate(-position.x, -position.y).toString(),
                );
            }
            const res = getPathOffset(data.fills, data.stroke);
            layer.width = data.naturalWidth + res.offsetWidth;
            layer.height = data.naturalHeight + res.offsetHeight;
            layer.left = Math.floor(position.x - res.translate);
            layer.top = Math.floor(position.y - res.translate);
        } else if (brushType === BrushType.Line) {
            const startPoint = mousePoints.current[0];
            const endPoint = mousePoints.current[mousePoints.current.length - 1];
            const info = getBrushBaseInfo(identity, brushData!, scale, startPoint, endPoint, stageIndex, false);
            data.__drawPath = generateLinePath({ x: 0, y: 0 }, { x: info.width, y: 0 });
            data.naturalWidth = info.width;
            data.naturalHeight = 1;
            const res = getPathOffset(data.fills, data.stroke);
            layer.width = data.naturalWidth + res.offsetWidth;
            layer.height = data.naturalHeight + res.offsetHeight;
            layer.left = Math.floor(info.left - res.translate);
            layer.top = Math.floor(info.top - res.translate);
            layer.rotate = info.rotate;
        }

        addVipMaterialIds(layer, brushData);

        const vectorLayer = convertBrushToVectorLayer(layer);

        const resLayer = new BrushLayer(identity).add({
            layer: vectorLayer || layer,
            trackFunc: trackConfig[LayerType.BRUSH_LAYER][`add_${brushType}`],
        });

        framework.events.emit(DRAW_EVENTS.DRAW_END, brushType);
        clearDrawConfig();

        // 如果转成了svg, 不需要生成缩略图
        if (vectorLayer) return;
        updateScreenshotUrl(identity, resLayer.id);
    }

    /**
     * 获取路径的尺寸
     *
     * @param path - SVG 路径
     */
    function getPathTransform(path: string, scale: number) {
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.style.left = '0';
        div.style.top = '0';
        div.style.visibility = 'hidden';
        div.style.pointerEvents = 'none';
        const divInner = document.createElement('div');
        divInner.innerHTML = `<svg>${path}</svg>`;
        div.appendChild(divInner);

        const contentNode = document.getElementById(`${identity}-brush-draw`);
        contentNode?.appendChild(div);

        const bound = divInner.querySelector('path')?.getBoundingClientRect()!;
        const width = bound.width || Math.abs(bound.right - bound.left);
        const height = bound.height || Math.abs(bound.top - bound.bottom);
        const left = bound.left || 0;
        const top = bound.top || 0;
        div.remove();

        return {
            width: Math.ceil(width / scale) || 1,
            height: Math.ceil(height / scale) || 1,
            left,
            top,
        };
    }

    if (!brushData) return null;

    const size = framework.getSizeByIndex(activeStageIndex);

    return (
        <BaseStagePlugin originIndexOrLayerId={activeOriginIndex} name="markup-draw">
            <RenderPen identity={identity} activeStageIndex={activeStageIndex} pen={penPoint} />
            <div
                id={`${identity}-brush-draw`}
                style={{
                    width: size.width,
                    height: size.height,
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: `translate(-50%, -50%) scale(${scale})`,
                }}
            >
                {previewLayer ? (
                    <>
                        {isVectorLayer.current ? (
                            <VectorLayer layer={previewLayer} identity={identity} isNavigator={true} />
                        ) : (
                            <BrushLayerView layer={previewLayer} identity={identity} isNavigator={false} />
                        )}
                    </>
                ) : null}
            </div>
        </BaseStagePlugin>
    );
});

const RenderPen: React.FC<{
    identity: string;
    activeStageIndex: number;
    pen: IPoint | null;
}> = React.memo(({ identity, activeStageIndex, pen }) => {
    const customCursor = pen ? convertToStagePoint(identity, pen, activeStageIndex) : null;
    return (
        <div
            className={classNames('brush-draw-mask', { crosshair: !customCursor })}
            data-operate-type={BaseLayerViewType.MASK}
        >
            {customCursor && (
                <div
                    className="brush-draw-mask-pen"
                    style={{ transform: `translate(${customCursor.x}px, ${customCursor.y}px)` }}
                >
                    <img src={CurPenSvg} width={16} height={16} />
                </div>
            )}
        </div>
    );
});

const DrawPlugin: React.FC = React.memo(() => {
    const drawConfig = useBrushLayerState()((state) => state.drawConfig);
    const identity = useIdentity();
    const isInit = useRef(false);
    const framework = getFramework(identity);

    useEffect(() => {
        if (!isInit.current) {
            isInit.current = true;
            return;
        }
        framework.events.emit(DRAW_EVENTS.CONFIG_CHANGE, drawConfig);
    }, [drawConfig]);

    /** 注册绘制快捷键 */
    useEffect(() => {
        // 绘制直线
        const lHandler = framework.events.on(HOTKEY_TYPE.L, () => {
            getBrushLayerState(identity).setDrawConfig({
                brushType: BrushType.Line,
                fills: [
                    {
                        ...getDefaultBrushAttribute(),
                        color: '#797c81ff',
                        width: 9,
                        linecap: BrushLinecapType.Round,
                        dasharray: BrushDasharrayType.Solid,
                    },
                ],
                __isQuickDraw: true,
            } as IBrushDrawConfig);
        });
        // 绘制圆
        const oHandler = framework.events.on(HOTKEY_TYPE.O, () => {
            getBrushLayerState(identity).setDrawConfig({
                brushType: BrushType.Ellipse,
                stroke: getDefaultBrushAttribute(),
                fills: [
                    {
                        ...getDefaultBrushAttribute(),
                        color: '#797c81ff',
                    },
                ],
                __isQuickDraw: true,
            } as IBrushDrawConfig);
        });
        // 绘制矩形
        const rHandler = framework.events.on(HOTKEY_TYPE.R, () => {
            getBrushLayerState(identity).setDrawConfig({
                brushType: BrushType.Rect,
                stroke: getDefaultBrushAttribute(),
                fills: [
                    {
                        ...getDefaultBrushAttribute(),
                        color: '#797c81ff',
                    },
                ],
                radius: 0,
                __isQuickDraw: true,
            } as IBrushDrawConfig);
        });

        return () => {
            framework.events.off(lHandler);
            framework.events.off(oHandler);
            framework.events.off(rHandler);
        };
    }, []);

    return drawConfig ? <DrawOperate /> : null;
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 9,
    name: 'brush-draw',
    render() {
        return <DrawPlugin />;
    },
    createState() {
        return [brushDrawPluginState];
    },
    createInstance({ identity }) {
        return [{ key: 'BrushDrawPlugin', value: new BrushDrawPlugin(identity) }];
    },
};

export default exportPlugin;
